import { usePageContext } from "vike-react/usePageContext";
import { ArrowLeft, Mail, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { products } from "@/data/products";
import { useLanguage } from "@/i18n/LanguageContext";
import { routes } from "@/i18n/routes";
import SeoMetadata from "@/components/SeoMetadata";

const categoryTranslationKeys: Record<string, string> = {
  "Sterowniki silnika": "catalog.categoryEngineECU",
  "Sterowniki skrzyni biegów": "catalog.categoryGearboxECU",
  "Sterowniki ABS/EBS": "catalog.categoryABSEBS",
  "Sterowniki klimatyzacji": "catalog.categoryClimate",
};

const ProductDetail = () => {
  const pageContext = usePageContext();
  const { slug } = pageContext.routeParams as { slug: string };
  const { language, t } = useLanguage();
  const product = products.find((p) => p.slug === slug);
  const description = (product?.description as any)?.[language] || (product?.description as any)?.["en"] || "";

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <main className="pt-40 pb-16 text-center">
          <p className="text-muted-foreground">{t("catalog.productNotFound")}</p>
          <a href={`/${language}/${routes.catalog[language]}`} className="text-accent hover:underline mt-4 inline-block">
            {t("catalog.backToCatalog")}
          </a>
        </main>
      </div>
    );
  }

  const mailtoSubject = encodeURIComponent(
    t("catalog.mailSubject").replace("{product}", product.title)
  );

  const formatMailBody = () => {
    let body = t("catalog.mailBody");
    const replacements: Record<string, string | number | undefined> = {
      "{title}": product.title,
      "{sku}": product.sku,
      "{manufacturer}": product.manufacturer,
      "{model}": product.model,
      "{engine}": product.engine,
      "{euro}": product.euro,
      "{kw}": product.kw,
      "{otherReferences}": product.otherReferences
    };

    for (const [key, value] of Object.entries(replacements)) {
      body = body.replace(key, String(value || ""));
    }
    return encodeURIComponent(body);
  };

  const mailtoBody = formatMailBody();
  const seoTitle = `${product.title} | Gendera Service`;

  return (
    <div className="min-h-screen bg-background">
      <SeoMetadata title={seoTitle} description={description} type="article" />
      <div className="pt-32 pb-16">
        <div className="container mx-auto px-4">
          <a href={`/${language}/${routes.catalog[language]}`} className="inline-flex items-center text-accent hover:text-accent/80 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("catalog.backToCatalog")}
          </a>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-secondary rounded-lg flex items-center justify-center overflow-hidden">
              {product.image ? (
                <img src={product.image} alt={product.title} className="w-full h-full object-contain"
                style={{ backgroundColor: product.imageBackground || 'white' }} />
              ) : (
                <Package className="w-32 h-32 text-muted-foreground/20" />
              )}
            </div>

            <div>
              <p className="text-sm font-semibold text-accent mb-2">{product.manufacturer}</p>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-4">{product.title}</h1>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {description}
              </p>

              <div className="bg-secondary/50 rounded-lg p-6 mb-8 border">
                <h2 className="font-semibold text-foreground mb-4 text-lg border-b pb-2">{t("catalog.specifications")}</h2>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4 text-sm">
                  <div className="col-span-1 sm:col-span-2 grid grid-cols-2 py-1 border-b border-border/50 last:border-0">
                    <dt className="text-muted-foreground">{t("catalog.specManufacturer")}</dt>
                    <dd className="text-foreground font-medium text-right">{product.manufacturer}</dd>
                  </div>
                  {product.model && (
                    <div className="col-span-1 sm:col-span-2 grid grid-cols-2 py-1 border-b border-border/50 last:border-0">
                      <dt className="text-muted-foreground">{t("catalog.specModel")}</dt>
                      <dd className="text-foreground font-medium text-right">{product.model}</dd>
                    </div>
                  )}
                   {product.euro && (
                    <div className="col-span-1 sm:col-span-2 grid grid-cols-2 py-1 border-b border-border/50 last:border-0">
                      <dt className="text-muted-foreground">Euro</dt>
                      <dd className="text-foreground font-medium text-right">{product.euro}</dd>
                    </div>
                  )}
                  {product.engine && (
                    <div className="col-span-1 sm:col-span-2 grid grid-cols-2 py-1 border-b border-border/50 last:border-0">
                      <dt className="text-muted-foreground">{t("catalog.specEngine")}</dt>
                      <dd className="text-foreground font-medium text-right">{product.engine}</dd>
                    </div>
                  )}
                   {product.kw && (
                    <div className="col-span-1 sm:col-span-2 grid grid-cols-2 py-1 border-b border-border/50 last:border-0">
                      <dt className="text-muted-foreground">{t("catalog.specPower")}</dt>
                      <dd className="text-foreground font-medium text-right">{product.kw} KW</dd>
                    </div>
                  )}
                  <div className="col-span-1 sm:col-span-2 grid grid-cols-2 py-1 border-b border-border/50 last:border-0">
                    <dt className="text-muted-foreground">SKU</dt>
                    <dd className="text-foreground font-medium text-right">{product.sku}</dd>
                  </div>
                   {product.otherReferences && (
                    <div className="col-span-1 sm:col-span-2 grid grid-cols-2 py-1 border-b border-border/50 last:border-0">
                      <dt className="text-muted-foreground">{t("catalog.specOtherRef")}</dt>
                      <dd className="text-foreground font-medium text-right break-all">{product.otherReferences}</dd>
                    </div>
                  )}
                  <div className="col-span-1 sm:col-span-2 grid grid-cols-2 py-1 border-b border-border/50 last:border-0">
                    <dt className="text-muted-foreground">{t("catalog.specCategory")}</dt>
                    <dd className="text-foreground font-medium text-right">
                      {categoryTranslationKeys[product.category] ? t(categoryTranslationKeys[product.category] as any) : product.category}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="bg-muted/30 p-6 rounded-lg border">
                <Button asChild size="lg" className="w-full bg-accent hover:bg-orange-hover text-accent-foreground font-semibold mb-4 text-lg h-12 shadow-md hover:shadow-lg transition-all">
                  <a href={`mailto:biuro@gendera-service.pl?subject=${mailtoSubject}&body=${mailtoBody}`}>
                    <Mail className="w-5 h-5 mr-2" />
                    {t("catalog.requestPrice")}
                  </a>
                </Button>

                <p className="text-sm text-muted-foreground leading-relaxed text-center italic">
                  {t("catalog.priceDisclaimer")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
